// src/pages/factors/Print.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../core/api";
import "./Print.css";

/* ============ استایل بولد (اولویت بالا، مستقل از Print.css) ============ */
const BOLD_CSS = `
  .bold-num,
  .digit-box,
  .serial-date-input,
  .items-table tbody td,
  .items-table thead th.bold-th {
    font-family: 'Vazirmatn', Tahoma, sans-serif !important;
    font-weight: 800 !important;
    font-synthesis: weight;
  }
  .items-table thead tr:first-child th {
    font-weight: 800 !important;
  }
`;

/* ============ توابع کمکی ============ */
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

const toFa = (num) => {
  if (num === undefined || num === null) return "";
  return String(num).replace(/\d/g, (d) => FA_DIGITS[+d]);
};

/* ساخت آرایه کاراکترها؛ دقیقاً به تعداد حروف/ارقام مقدار */
function digits(str) {
  const s = str === undefined || str === null ? "" : String(str).trim();
  return toFa(s).split("");
}

/* تبدیل عدد به حروف فارسی */
function numberToPersianWords(num) {
  if (num === undefined || num === null || isNaN(num)) return "";
  num = Math.floor(Number(num));
  if (num === 0) return "صفر";

  const yekan = ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"];
  const dahgan = [
    "",
    "",
    "بیست",
    "سی",
    "چهل",
    "پنجاه",
    "شصت",
    "هفتاد",
    "هشتاد",
    "نود",
  ];
  const dah = [
    "ده",
    "یازده",
    "دوازده",
    "سیزده",
    "چهارده",
    "پانزده",
    "شانزده",
    "هفده",
    "هجده",
    "نوزده",
  ];
  const sadgan = [
    "",
    "صد",
    "دویست",
    "سیصد",
    "چهارصد",
    "پانصد",
    "ششصد",
    "هفتصد",
    "هشتصد",
    "نهصد",
  ];
  const scales = ["", " هزار", " میلیون", " میلیارد", " بیلیون"];

  function threeDigitToWords(n) {
    const parts = [];
    const sad = Math.floor(n / 100);
    const rem = n % 100;
    if (sad > 0) parts.push(sadgan[sad]);
    if (rem >= 10 && rem < 20) {
      parts.push(dah[rem - 10]);
    } else {
      const dahg = Math.floor(rem / 10);
      const yek = rem % 10;
      if (dahg > 0) parts.push(dahgan[dahg]);
      if (yek > 0) parts.push(yekan[yek]);
    }
    return parts.join(" و ");
  }

  const result = [];
  let scaleIndex = 0;
  while (num > 0) {
    const chunk = num % 1000;
    if (chunk > 0) {
      result.unshift(threeDigitToWords(chunk) + scales[scaleIndex]);
    }
    num = Math.floor(num / 1000);
    scaleIndex++;
  }
  return result.join(" و ");
}

/* ============ کامپوننت جعبه‌های ارقام ============ */
function DigitBoxes({ value }) {
  const arr = digits(value);
  if (arr.length === 0) return <div className="digit-boxes" />;
  return (
    <div className="digit-boxes">
      {arr.map((d, i) => (
        <div className="digit-box" key={i}>
          {d}
        </div>
      ))}
    </div>
  );
}

/* ============ کامپوننت اصلی ============ */
export default function Print() {
  const { id } = useParams();
  const [factor, setFactor] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");

  const frameRef = useRef(null);
  const invoiceRef = useRef(null);

  useEffect(() => {
    if (factor) document.title = `فاکتور ${factor.serial} | پنل فاکتور`;
  }, [factor]);

  /* ============== دریافت داده‌ها ============== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [factorRes, companyRes] = await Promise.all([
          api.get(`/factors/${id}`),
          api.get("/company"),
        ]);

        if (cancelled) return;

        const f = factorRes.data?.factor || factorRes.data;
        const s = companyRes.data?.company || companyRes.data || {};

        if (!f || !f.id) {
          setNotFound(true);
          return;
        }

        setFactor(f);
        setSeller(s);
      } catch (err) {
        if (cancelled) return;
        console.error("خطا در دریافت اطلاعات:", err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("خطا در دریافت اطلاعات فاکتور");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ============== محاسبه اندازه برای فیت در A4 ============== */
  useEffect(() => {
    if (!factor || loading) return;

    const fitToPage = () => {
      const frame = frameRef.current;
      const inv = invoiceRef.current;
      if (!frame || !inv) return;

      inv.style.transform = "none";
      const frameHeight = frame.clientHeight;
      const naturalHeight = inv.scrollHeight;

      if (naturalHeight > frameHeight && frameHeight > 0) {
        const scale = frameHeight / naturalHeight;
        inv.style.transformOrigin = "top center";
        inv.style.transform = `scale(${scale})`;
      } else {
        inv.style.transform = "none";
      }
    };

    const t1 = setTimeout(fitToPage, 100);
    const t2 = setTimeout(fitToPage, 400);

    window.addEventListener("resize", fitToPage);
    window.addEventListener("beforeprint", fitToPage);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitToPage);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fitToPage);
      window.removeEventListener("beforeprint", fitToPage);
    };
  }, [factor, loading]);

  /* ============== حالات خطا ============== */
  if (loading) {
    return (
      <div className="print-loading">
        <div className="spinner" />
        <p>در حال بارگذاری فاکتور...</p>
        <style>{`
          .print-loading {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 16px;
            font-family: 'Vazirmatn', Tahoma, sans-serif;
          }
          .spinner {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            border: 3px solid var(--border-strong, #e5e5ea);
            border-top-color: var(--accent, #0a84ff);
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
          .print-loading p {
            color: var(--muted, #6e6e73);
            font-size: 14px;
          }
        `}</style>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="print-loading">
        <div style={{ fontSize: 44, color: "var(--muted, #6e6e73)" }}>
          <i className="fa-solid fa-triangle-exclamation" />
        </div>
        <h2 style={{ margin: 0 }}>فاکتور یافت نشد</h2>
        <p style={{ color: "var(--muted, #6e6e73)" }}>
          فاکتوری با این شناسه وجود ندارد یا حذف شده است.
        </p>
        <Link
          to="/dashboard/factors"
          className="btn secondary"
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            background: "#0a84ff",
            color: "#fff",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          <i className="fa-solid fa-arrow-right" /> بازگشت به لیست
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-loading">
        <div style={{ fontSize: 44, color: "var(--muted, #6e6e73)" }}>
          <i className="fa-solid fa-triangle-exclamation" />
        </div>
        <h2 style={{ margin: 0 }}>{error}</h2>
        <Link
          to="/dashboard/factors"
          style={{
            padding: "10px 20px",
            borderRadius: 10,
            background: "#0a84ff",
            color: "#fff",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          <i className="fa-solid fa-arrow-right" /> بازگشت به لیست
        </Link>
      </div>
    );
  }

  /* ============== محاسبه جمع‌ها ============== */
  const items = factor.items || [];
  let sumGross = 0;
  let sumDisc = 0;
  let sumAfterDisc = 0;
  let sumTax = 0;
  let sumLineTotal = 0;

  items.forEach((it) => {
    const lineGross = Number(it.qty) * Number(it.price);
    const disc = Number(it.discount) || 0;
    const tax = Number(it.tax) || 0;
    const afterDisc = lineGross - disc;
    const lineTotal = afterDisc + tax;

    sumGross += lineGross;
    sumDisc += disc;
    sumAfterDisc += afterDisc;
    sumTax += tax;
    sumLineTotal += lineTotal;
  });

  const buyer = factor.buyer || {};
  const sellerData = seller || {};

  return (
    <>
      <style>{BOLD_CSS}</style>

      {/* ============ دکمه‌های اکشن ============ */}
      <div className="print-actions">
        <button type="button" onClick={() => window.print()}>
          <i className="fa-solid fa-print" /> چاپ
        </button>
        <Link to="/dashboard/factors">
          <i className="fa-solid fa-arrow-right" /> بازگشت
        </Link>
      </div>

      {/* ============ قالب فاکتور ============ */}
      <div className="page-frame" ref={frameRef} id="pageFrame">
        <div
          className="invoice-container"
          ref={invoiceRef}
          id="invoiceContainer"
        >
          {/* ============ هدر ============ */}
          <div className="header-top">
            <div style={{ width: 220 }} />
            <div className="header-title">صورتحساب فروش کالا و خدمات</div>
            <div className="serial-date-container">
              <div className="serial-date-row">
                <span className="serial-date-label">شماره سریال:</span>
                <div className="serial-date-input">{toFa(factor.serial)}</div>
              </div>
              <div className="serial-date-row">
                <span className="serial-date-label">تاریخ:</span>
                <div className="serial-date-input">{toFa(factor.date)}</div>
              </div>
            </div>
          </div>

          {/* ============ اطلاعات فروشنده ============ */}
          <div className="info-box">
            <div className="info-title">مشخصات فروشنده:</div>

            <div className="info-row">
              <span style={{ width: 120 }}>نام شخص حقیقی/حقوقی:</span>
              <div className="field-fill" style={{ marginLeft: 15 }}>
                {sellerData.name || ""}
              </div>

              <span>شماره اقتصادی:</span>
              <DigitBoxes value={sellerData.eco} />

              <span style={{ marginRight: 15 }}>شماره ثبت/شماره ملی:</span>
              <DigitBoxes value={sellerData.national} />
            </div>

            <div className="info-row" style={{ marginTop: 8 }}>
              <span>نشانی کامل: استان:</span>
              <div
                className="field-fill"
                style={{ maxWidth: 100, marginLeft: 10 }}
              >
                {sellerData.province || ""}
              </div>
              <span>شهرستان:</span>
              <div
                className="field-fill"
                style={{ maxWidth: 100, marginLeft: 10 }}
              >
                {sellerData.city || ""}
              </div>
              <span>کد پستی ده رقمی:</span>
              <DigitBoxes value={sellerData.postal} />
              <span style={{ marginRight: 10 }}>شهر:</span>
              <div className="field-fill" style={{ maxWidth: 100 }}>
                {sellerData.city || ""}
              </div>
            </div>

            <div className="info-row" style={{ marginTop: 8 }}>
              <span>نشانی:</span>
              <div className="field-fill" style={{ marginLeft: 20 }}>
                {sellerData.address || ""}
              </div>
              <span>شماره تلفن/نمابر:</span>
              <div className="field-fill bold-num" style={{ maxWidth: 200 }}>
                {toFa(sellerData.phone) || ""}
              </div>
            </div>
          </div>

          {/* ============ اطلاعات خریدار ============ */}
          <div className="info-box">
            <div className="info-title">مشخصات خریدار:</div>

            <div className="info-row">
              <span style={{ width: 120 }}>نام شخص حقیقی/حقوقی:</span>
              <div className="field-fill" style={{ marginLeft: 15 }}>
                {buyer.name || ""}
              </div>

              <span>شماره اقتصادی:</span>
              <DigitBoxes value={buyer.eco} />

              <span style={{ marginRight: 15 }}>شماره ثبت/شماره ملی:</span>
              <DigitBoxes value={buyer.national} />
            </div>

            <div className="info-row" style={{ marginTop: 8 }}>
              <span>نشانی کامل: استان:</span>
              <div
                className="field-fill"
                style={{ maxWidth: 100, marginLeft: 10 }}
              >
                {buyer.province || ""}
              </div>
              <span>شهرستان:</span>
              <div
                className="field-fill"
                style={{ maxWidth: 100, marginLeft: 10 }}
              >
                {buyer.city || ""}
              </div>
              <span>کد پستی ده رقمی:</span>
              <DigitBoxes value={buyer.postal} />
              <span style={{ marginRight: 10 }}>شهر:</span>
              <div className="field-fill" style={{ maxWidth: 100 }}>
                {buyer.city || ""}
              </div>
            </div>

            <div className="info-row" style={{ marginTop: 8 }}>
              <span>نشانی:</span>
              <div className="field-fill" style={{ marginLeft: 20 }}>
                {buyer.address || ""}
              </div>
              <span>شماره تلفن/نمابر:</span>
              <div className="field-fill bold-num" style={{ maxWidth: 200 }}>
                {toFa(buyer.phone) || ""}
              </div>
            </div>
          </div>

          {/* ============ عنوان جدول ============ */}
          <div className="table-section-title">
            مشخصات کالا یا خدمات مورد معامله
          </div>

          {/* ============ جدول اقلام ============ */}
          <table className="items-table">
            <thead>
              <tr>
                <th className="col-1 num-header">۱</th>
                <th className="col-2 num-header">۲</th>
                <th className="col-3 num-header">۳</th>
                <th className="col-4 num-header">۴</th>
                <th className="col-5 num-header">۵</th>
                <th className="col-6 num-header">۶</th>
                <th className="col-7 num-header">۷</th>
                <th className="col-8 num-header">۸</th>
                <th className="col-9 num-header">۹</th>
                <th className="col-10 num-header">۱۰</th>
                <th className="col-11 num-header">۱۱</th>
              </tr>
              <tr>
                <th>ردیف</th>
                <th>کد کالا</th>
                <th className="bold-th">شرح کالا یا خدمات</th>
                <th>تعداد/مقدار</th>
                <th>واحد اندازه گیری</th>
                <th>مبلغ واحد (ریال)</th>
                <th>مبلغ کل (ریال)</th>
                <th>مبلغ تخفیف (ریال)</th>
                <th>مبلغ کل پس از تخفیف (ریال)</th>
                <th>جمع مالیات و عوارض (ریال)</th>
                <th>جمع مبلغ کل بعلاوه جمع مالیات و عوارض (ریال)</th>
              </tr>
            </thead>
            <tbody>
              {/* ردیف توضیحات */}
              {factor.notes && (
                <tr className="description-row">
                  <td colSpan="3">{factor.notes}</td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              )}

              {/* ردیف‌های اقلام */}
              {items.map((it, i) => {
                const lineGross = Number(it.qty) * Number(it.price);
                const afterDisc = lineGross - Number(it.discount);
                const lineTotal = afterDisc + Number(it.tax);

                return (
                  <tr key={i}>
                    <td>{toFa(i + 1)}</td>
                    <td>{it.code ? toFa(it.code) : "-"}</td>
                    <td>{it.name}</td>
                    <td>{toFa(it.qty)}</td>
                    <td>{it.unit || "-"}</td>
                    <td>{Number(it.price).toLocaleString("fa-IR")}</td>
                    <td>{Number(lineGross).toLocaleString("fa-IR")}</td>
                    <td>{Number(it.discount).toLocaleString("fa-IR")}</td>
                    <td>{Number(afterDisc).toLocaleString("fa-IR")}</td>
                    <td>{Number(it.tax).toLocaleString("fa-IR")}</td>
                    <td>{Number(lineTotal).toLocaleString("fa-IR")}</td>
                  </tr>
                );
              })}

              {/* ردیف‌های خالی (تا ۹ ردیف) */}
              {Array.from({
                length: Math.max(0, 9 - items.length),
              }).map((_, e) => (
                <tr key={`empty-${e}`}>
                  <td>{toFa(items.length + e + 1)}</td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              ))}

              {/* ردیف جمع کل */}
              <tr className="total-row">
                <td
                  colSpan="6"
                  style={{ textAlign: "right", paddingRight: 15 }}
                >
                  جمع کل : {numberToPersianWords(sumLineTotal)} ریال
                </td>
                <td>{Number(sumGross).toLocaleString("fa-IR")}</td>
                <td>{Number(sumDisc).toLocaleString("fa-IR")}</td>
                <td>{Number(sumAfterDisc).toLocaleString("fa-IR")}</td>
                <td>{Number(sumTax).toLocaleString("fa-IR")}</td>
                <td>{Number(sumLineTotal).toLocaleString("fa-IR")}</td>
              </tr>
            </tbody>
          </table>

          {/* ============ فوتر ============ */}
          <div className="footer-section">
            <div className="terms-row">
              <span style={{ fontWeight: "bold", marginLeft: 20 }}>
                شرایط و نحوه فروش:
              </span>
              <div className="checkbox-item">
                <div
                  className={`checkbox ${
                    factor.paymentType === "نقدی" ? "selected" : ""
                  }`}
                />
                <span>نقدی</span>
              </div>
              <div className="checkbox-item">
                <div
                  className={`checkbox ${
                    factor.paymentType === "غیر نقدی" ? "selected" : ""
                  }`}
                />
                <span>غیر نقدی</span>
              </div>
            </div>

            <div className="terms-row" style={{ marginTop: 12 }}>
              <span style={{ fontWeight: "bold" }}>
                شماره شبا بانکی ( الزامی ) :
              </span>
              <span style={{ fontWeight: "bold", marginRight: 15 }}>
                به نام:
              </span>
              <div
                className="dotted-line"
                style={{
                  maxWidth: 250,
                  fontFamily: "Tahoma, sans-serif",
                  direction: "ltr",
                }}
              >
                {sellerData.holder_name || ""}
              </div>
              <div
                className="dotted-line"
                style={{
                  marginRight: 10,
                  maxWidth: 400,
                  fontWeight: "bold",
                  fontFamily: "Tahoma, sans-serif",
                  direction: "ltr",
                }}
              >
                {sellerData.iban || ""}
              </div>
            </div>

            {/* امضاها */}
            <div className="signature-section">
              <div className="signature-box">مهر و امضاء فروشنده</div>
              <div className="signature-box">مهر و امضاء خریدار</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
